/* eslint-disable camelcase */
import { GENERIC_CONCESSION_VALIDATION_ERROR } from './concessionValidationConstants'
import { hasConcessionVendorGridSectionFieldErrors } from './concessionVendorGridDefaults'
import { resolveFlagColorFromInput } from './flagColor'

export const CONCESSION_GALLERY_VARIANTS = {
  SPOTLIGHT: 'spotlight',
  FULL_CARD_CAROUSEL: 'full_card_carousel',
  MEDIA_ONLY_CAROUSEL: 'media_only_carousel'
}

export const CONCESSION_GALLERY_MIN_CAROUSEL_ITEMS = 2
export const CONCESSION_GALLERY_MAX_CAROUSEL_ITEMS = 5

export const isValidYouTubeUrl = url => {
  const value = String(url ?? '').trim()
  if (!value) {
    return false
  }
  return /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)/i.test(value)
}

export const getDefaultConcessionGalleryItem = () => ({
  src: '',
  alt: '',
  mediaType: 'image',
  badge: null,
  title: '',
  subtitle: '',
  cta: {
    label: '',
    url: '',
    target: '_self'
  }
})

export const getDefaultConcessionBadge = () => ({
  title: '',
  color: ''
})

export const normalizeConcessionBadgeColor = (color, legacyBackgroundColor) => {
  if (typeof color === 'string' && color.trim()) {
    const trimmed = color.trim()
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`
  }

  if (color && typeof color === 'object' && color.value) {
    const value = String(color.value).trim()
    return value.startsWith('#') ? value : `#${value}`
  }

  if (legacyBackgroundColor) {
    const resolved = resolveFlagColorFromInput(legacyBackgroundColor)
    return resolved?.value || ''
  }

  return ''
}

export const normalizeConcessionBadge = (badge = null) => {
  if (!badge) {
    return null
  }

  if (typeof badge === 'string') {
    const title = badge.trim()
    return title ? { title, color: '' } : null
  }

  if (typeof badge !== 'object') {
    return null
  }

  const title = String(badge.title ?? badge.label ?? '').trim()
  const color = normalizeConcessionBadgeColor(
    badge.color,
    badge.background_color ?? badge.backgroundColor
  )
  if (!title && !color) {
    return null
  }

  return {
    title,
    color
  }
}

export const normalizeConcessionGalleryItem = (item = {}) => {
  const cta = item?.cta && typeof item.cta === 'object'
    ? {
      label: String(item.cta.label ?? '').trim(),
      url: String(item.cta.url ?? item.cta.href ?? '').trim(),
      target: item.cta.target || '_self'
    }
    : null

  return {
    src: String(item?.src ?? item?.url ?? '').trim(),
    alt: String(item?.alt ?? '').trim(),
    mediaType: item?.mediaType === 'video' ? 'video' : 'image',
    badge: normalizeConcessionBadge(item?.badge),
    title: item?.title != null ? String(item.title).trim() : '',
    subtitle: item?.subtitle != null ? String(item.subtitle).trim() : '',
    cta: cta?.label || cta?.url ? cta : null
  }
}

export const normalizeConcessionGalleryItems = (galleryItems = []) => (
  Array.isArray(galleryItems) ? galleryItems.map(normalizeConcessionGalleryItem) : []
)

export const getConcessionGalleryItemLimits = variantType => {
  if (variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT) {
    return { min: 1, max: 1 }
  }

  return {
    min: CONCESSION_GALLERY_MIN_CAROUSEL_ITEMS,
    max: CONCESSION_GALLERY_MAX_CAROUSEL_ITEMS
  }
}

export const getConcessionGalleryItemFieldErrors = (item = {}, variantType = CONCESSION_GALLERY_VARIANTS.SPOTLIGHT) => {
  const normalizedItem = normalizeConcessionGalleryItem(item)
  const errors = {
    src: '',
    title: '',
    subtitle: '',
    videoUrl: ''
  }

  if (normalizedItem.mediaType === 'image') {
    if (!normalizedItem.src) {
      errors.src = 'Image is required'
    }
  } else if (!normalizedItem.src) {
    errors.src = 'Video URL is required'
  } else if (!isValidYouTubeUrl(normalizedItem.src)) {
    errors.videoUrl = 'Only YouTube video URLs are accepted'
  }

  if (variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT) {
    if (!normalizedItem.title) {
      errors.title = 'Spotlight header is required'
    }
    if (!normalizedItem.subtitle) {
      errors.subtitle = 'Spotlight description is required'
    }
  } else if (variantType === CONCESSION_GALLERY_VARIANTS.FULL_CARD_CAROUSEL && !normalizedItem.title) {
    errors.title = 'Carousel card header is required'
  }

  return errors
}

export const getConcessionGallerySectionFieldErrors = ({
  enabled = true,
  title = '',
  type = CONCESSION_GALLERY_VARIANTS.SPOTLIGHT,
  galleryItems = [],
  requireSectionHeader = true
} = {}) => {
  if (enabled === false) {
    return {
      title: '',
      cardCount: '',
      items: []
    }
  }

  const variantType = type || CONCESSION_GALLERY_VARIANTS.SPOTLIGHT
  const items = Array.isArray(galleryItems) ? galleryItems : []
  const { min, max } = getConcessionGalleryItemLimits(variantType)
  let cardCount = ''

  if (items.length < min) {
    cardCount = variantType === CONCESSION_GALLERY_VARIANTS.SPOTLIGHT
      ? 'Spotlight requires 1 item'
      : `Carousel requires at least ${min} cards`
  } else if (items.length > max) {
    cardCount = `Carousel supports up to ${max} cards`
  }

  return {
    title: requireSectionHeader && !String(title ?? '').trim() ? 'Section header is required' : '',
    cardCount,
    items: items.map(item => getConcessionGalleryItemFieldErrors(item, variantType))
  }
}

export const hasConcessionGallerySectionFieldErrors = (fieldErrors = {}) => {
  if (fieldErrors.title || fieldErrors.cardCount) {
    return true
  }

  return (fieldErrors.items || []).some(itemErrors => Object.values(itemErrors || {}).some(Boolean))
}

export const validateConcessionFeaturedVendorSection = (section = {}) => {
  const fieldErrors = getConcessionGallerySectionFieldErrors(section)

  if (hasConcessionGallerySectionFieldErrors(fieldErrors)) {
    return {
      isValid: false,
      message: GENERIC_CONCESSION_VALIDATION_ERROR,
      fieldErrors
    }
  }

  return { isValid: true, fieldErrors }
}

export const validateConcessionMediaOnlyCarouselFooter = (section = {}) => {
  if (section.enabled === false) {
    return { isValid: true, fieldErrors: { title: '', cardCount: '', items: [] } }
  }

  const fieldErrors = getConcessionGallerySectionFieldErrors({
    ...section,
    type: CONCESSION_GALLERY_VARIANTS.MEDIA_ONLY_CAROUSEL,
    requireSectionHeader: false
  })

  if (hasConcessionGallerySectionFieldErrors(fieldErrors)) {
    return {
      isValid: false,
      message: GENERIC_CONCESSION_VALIDATION_ERROR,
      fieldErrors
    }
  }

  return { isValid: true, fieldErrors }
}

export const reconcileGalleryItemsForVariant = (galleryItems = [], variantType = CONCESSION_GALLERY_VARIANTS.SPOTLIGHT) => {
  const normalizedItems = normalizeConcessionGalleryItems(galleryItems)
  const { min, max } = getConcessionGalleryItemLimits(variantType)
  let nextItems = normalizedItems.slice(0, max)

  while (nextItems.length < min) {
    nextItems = [...nextItems, getDefaultConcessionGalleryItem()]
  }

  return nextItems
}

export const hasConcessionFormErrors = (errors = {}) => (
  Object.entries(errors).some(([key, value]) => {
    if (key === 'featuredVendor' || key === 'mediaCarouselFooter') {
      return hasConcessionGallerySectionFieldErrors(value)
    }

    if (key === 'vendorGrid') {
      return hasConcessionVendorGridSectionFieldErrors(value)
    }

    if (Array.isArray(value)) {
      return value.some(item => Object.values(item || {}).some(Boolean))
    }

    if (value && typeof value === 'object') {
      return Object.values(value).some(Boolean)
    }

    return Boolean(value)
  })
)
